// @ts-strict-ignore
import { createSelector } from 'reselect';
import { RootState } from '@/reducers/reducers';
import {
    OrgSummaryPreferences,
    SummaryViewResponseAPI,
    SummaryViewTableRow,
    OrgDestinationsResponse,
    SummaryViewPageCacheEntry,
} from '@/types/summaryView';
import { transformToTableRows, processDestinations, processVersions } from '@/helpers/summaryViewHelpers';
import { Destination } from '@/types/destinations';

// Org-level selectors
export const selectDestinationsData = (state: RootState, orgId: string): OrgDestinationsResponse | undefined => {
    if (state.ui?.uiConfig?.demoData?.orgDestinations?.[orgId]) {
        return state.ui?.uiConfig?.demoData?.orgDestinations?.[orgId];
    }

    return state.entities.summaryView.orgDestinations?.[orgId];
};

export const selectCanAccessTabularView = (state: RootState, orgId: string): boolean =>
    selectDestinationsData(state, orgId)?.canAccessTabularView ?? false;

export const selectTabularViewDestinationLimit = (state: RootState, orgId: string): number | undefined =>
    selectDestinationsData(state, orgId)?.tabularViewDestinationLimit;

// App-level selectors
export const selectShouldRenderWorkflowsOverviewForApp = (state: RootState, orgId: string, appId: string): boolean => {
    if (state.ui?.uiConfig?.demoData?.appsDemoData?.[appId]) {
        return !!state.ui.uiConfig.demoData.appsDemoData[appId].app.workflowOverview;
    }

    const orgDestinations = selectDestinationsData(state, orgId);
    return orgDestinations?.appDestinations?.find((app) => app.appId === appId)?.renderWorkflowsOverview ?? false;
};

// Shared selectors (work for org or app level)
export const selectAvailableDestinations = createSelector(
    [selectDestinationsData, (_state: RootState, _orgId: string, appId?: string) => appId],
    (orgData, appId): Destination[] => processDestinations(orgData, appId),
);

export const selectAvailableDestinationIds = createSelector([selectAvailableDestinations], (destinations): string[] =>
    destinations.map((d) => d.stepConfigId),
);

export const selectAvailableVersions = createSelector(
    [selectDestinationsData, (_state: RootState, _orgId: string, appId?: string) => appId],
    (orgData, appId): string[] => processVersions(orgData, appId),
);

export const selectActiveVersion = createSelector(
    [selectDestinationsData, (_state: RootState, _orgId: string, appId?: string) => appId],
    (orgData, appId): string | null => {
        if (!orgData?.appDestinations) return null;
        const relevantApps =
            appId ? orgData.appDestinations.filter((app) => app.appId === appId) : orgData.appDestinations;
        return relevantApps[0]?.activeVersion ?? null;
    },
);

export const selectLiveVersion = createSelector(
    [selectDestinationsData, (_state: RootState, _orgId: string, appId?: string) => appId],
    (orgData, appId): string | null => {
        if (!orgData?.appDestinations) return null;
        const relevantApps =
            appId ? orgData.appDestinations.filter((app) => app.appId === appId) : orgData.appDestinations;
        return relevantApps[0]?.liveVersion ?? null;
    },
);

// Generic selectors (use requestId)
export const createSummaryRequestId = (orgId: string, appId?: string): string =>
    appId ? `org-summary-${orgId}-${appId}` : `org-summary-${orgId}`;

export const selectSummaryPreferencesState = (state: RootState) => state.orgSummaryPreferences;

export const selectSummaryPreferencesForApp = (state: RootState, appId?: string): OrgSummaryPreferences | undefined => {
    if (appId && state.ui?.uiConfig?.demoData?.summaryPreferences?.[appId]) {
        return state.ui.uiConfig.demoData.summaryPreferences[appId];
    }

    return appId ? state.orgSummaryPreferences.byAppId[appId] : undefined;
};

export const selectSummaryViewByRequestId = (
    state: RootState,
    requestId: string,
): SummaryViewResponseAPI | undefined => {
    return state.ui?.uiConfig?.demoData?.summaryViewByRequestId?.[requestId];
};

export const selectSummaryViewEntryByRequestId = (
    state: RootState,
    requestId: string,
): SummaryViewPageCacheEntry | undefined => {
    return state.entities.summaryView.byId?.[requestId];
};

export const selectTransformedSummaryRows = createSelector(
    [
        (state: RootState, requestId: string) =>
            selectSummaryViewEntryByRequestId(state, requestId)?.summaryView?.versionSummaryViewItems ??
            selectSummaryViewByRequestId(state, requestId)?.versionSummaryViewItems ??
            [],
    ],
    (items) => transformToTableRows(items),
);

export const selectFilteredSummaryRows = createSelector(
    [
        selectTransformedSummaryRows,
        (_state: RootState, _requestId: string, versions: string[]) => versions,
        (_state: RootState, _requestId: string, _versions: string[], destinationIds: string[]) => destinationIds,
    ],
    (rows, versions, destinationIds): SummaryViewTableRow[] =>
        rows.filter((row) => versions.includes(row.version) && destinationIds.includes(row.destinationId)),
);
